import { cn } from "@/lib/utils";
import type { FpMessage, FpMessagePart } from "../../../../schemas";
import { Avatar } from "../Avatar";
import { Attachments } from "../ChatId";
import { Markdown } from "../Markdown";
import { ResponseFeedback } from "../ResponseFeedback";
import { TypingIndicator } from "../TypingIndicator";
import styles from "./MessageContent.module.css";
import { MessageParts } from "./MessageParts";
import { RetryButton } from "./RetryButton";

const syntaxHighlighterOptions = {
  className: "rounded-md p-4 bg-muted",
};

type EphemeralMessageContentProps = {
  type: "ephemeral";
  id: string;
  sender: "user";
  content: FpMessage["content"];
  status: FpMessage["status"];
  isCancelled: boolean;
  hasFiles: boolean;
  originalMessage?: string;
  usesParts?: boolean;
  hasPartsContent?: boolean;
  parts?: FpMessagePart[];
  isWaitingForParts?: boolean;
  isAgentThinking?: boolean;
  isFinalMessage?: boolean;
  hasSystemMessage?: boolean;
};

type PersistedMessageContentProps = {
  type: "persisted";
  id: string;
  sender: FpMessage["sender"];
  content: FpMessage["content"];
  status: FpMessage["status"];
  isCancelled: boolean;
  hasFiles: boolean;
  originalMessage?: string;
  chatId: string;
  usesParts?: boolean;
  hasPartsContent?: boolean;
  parts?: FpMessagePart[];
  isWaitingForParts?: boolean;
  isAgentThinking?: boolean;
  isFinalMessage?: boolean;
  hasSystemMessage?: boolean;
};

const EMPTY_PARTS: FpMessagePart[] = [];

export function MessageContent(
  props: PersistedMessageContentProps | EphemeralMessageContentProps,
) {
  const {
    id,
    sender,
    content,
    status,
    isCancelled,
    isAgentThinking,
    hasFiles,
    originalMessage,
    type,
    usesParts = false,
    hasPartsContent = false,
    isWaitingForParts = false,
    parts = EMPTY_PARTS,
    isFinalMessage = false,
    hasSystemMessage = false,
  } = props;

  const isEmpty = usesParts
    ? !hasPartsContent && !hasFiles && !isWaitingForParts
    : !content && !hasFiles;

  return (
    <div className="grid grid-cols-[auto_minmax(0,1fr)] gap-[10px]" id={id}>
      <div className="w-7 h-7">
        <Avatar sender={sender} />
      </div>

      <div
        className={cn(
          "self-center flex flex-col gap-3",
          isCancelled && "text-muted-foreground",
          styles.messageContent,
        )}
      >
        {!isEmpty ? (
          <>
            {/* Use parts rendering for new messages, legacy for old messages */}
            {usesParts ? (
              hasPartsContent && parts ? (
                <>
                  <MessageParts parts={parts} messageId={id} />
                  {isAgentThinking && <TypingIndicator showText={false} />}
                </>
              ) : (
                <TypingIndicator />
              )
            ) : (
              <Markdown
                syntaxHighlighterOptions={syntaxHighlighterOptions}
                isStreaming={status === "processing" || status === "pending"}
                className="p-1"
              >
                {content}
              </Markdown>
            )}
            {isCancelled && !hasSystemMessage && sender === "agent" && (
              <div className="text-sm text-muted-foreground mt-2 italic">
                Message generation cancelled
              </div>
            )}
          </>
        ) : isCancelled ? (
          <div className="text-sm text-muted-foreground italic">
            Message generation cancelled
          </div>
        ) : (
          <TypingIndicator />
        )}

        {!usesParts && hasFiles && <Attachments messageId={id} />}

        {isCancelled &&
          sender === "agent" &&
          originalMessage &&
          isFinalMessage && <RetryButton originalMessage={originalMessage} />}

        {type === "persisted" &&
          sender === "agent" &&
          !isEmpty &&
          status === "completed" && (
            <div>
              <ResponseFeedback messageId={id} chatId={props.chatId} />
            </div>
          )}
      </div>
    </div>
  );
}
